import s from './Pagination.module.css';

interface IPaginationProps {
  page: number;
  total: number;
  limit: number;
  between?: number;
  ellipsis?: number;
  changePage: (page: number) => void;
}

export function Pagination({page, total, limit, between = 4, ellipsis = 1, changePage}:IPaginationProps) {
                                                        
  const totalPages = Math.ceil(total / limit);
  
  if (totalPages <= 1) {
    return null;
  }

  const getPageNumbers = (): (number | string)[] => {
    const pageNumbers: (number | string)[] = [];
    
    pageNumbers.push(1);
    
    const leftBound = Math.max(2, page - between);
    const rightBound = Math.min(totalPages - 1, page + between);
    
    if (leftBound > 2) {
      for (let i = 2; i < Math.min(ellipsis + 1, leftBound); i++) {
        pageNumbers.push(i);
      }
      if (leftBound > ellipsis + 1) {
        pageNumbers.push('ellipsis-left');
      } else if (leftBound === ellipsis + 1) {
        pageNumbers.push(leftBound - 1);
      }
    }
    
    for (let i = leftBound; i <= rightBound; i++) {
      pageNumbers.push(i);
    }
    
    if (rightBound < totalPages - 1) {
      if (rightBound < totalPages - ellipsis) {
        pageNumbers.push('ellipsis-right');
      } else if (rightBound === totalPages - ellipsis - 1) {
        pageNumbers.push(rightBound + 1);
      }
      for (let i = Math.max(rightBound + 1, totalPages - ellipsis + 1); i < totalPages; i++) {
        pageNumbers.push(i);
      }
    }
    
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers.filter((value, index, array) => 
      array.indexOf(value) === index
    );
  };

  const handlePageClick = (pageNumber: number) => {
    if (pageNumber !== page) {
      changePage(pageNumber);
    }
  };

  const handlePrevClick = () => {
    if (page > 1) {
      changePage(page - 1);
    }
  };

  const handleNextClick = () => {
    if (page < totalPages) {
      changePage(page + 1);
    }
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav className={s.pagination} aria-label="Навигация по страницам">
      <ul className={s.paginationList}>
        <li className={s.paginationItem}>
          <button
            className={`${s.paginationButton} ${page === 1 ? s.disabled : ''}`}
            onClick={handlePrevClick}
            disabled={page === 1}
            aria-label="Предыдущая страница"
          >
            {'<'}
          </button>
        </li>

        {pageNumbers.map((pageNumber, index) => {
          if (pageNumber === 'ellipsis-left' || pageNumber === 'ellipsis-right') {
            return (
              <li key={`ellipsis-${index}`} className={s.paginationItem}>
                <span className={s.paginationEllipsis}>...</span>
              </li>
            );
          }

          const num = pageNumber as number;
          return (
            <li key={num} className={s.paginationItem}>
              <button
                className={`${s.paginationButton} ${num === page ? s.active : ''}`}
                onClick={() => handlePageClick(num)}
                aria-label={`Страница ${num}`}
                aria-current={num === page ? 'page' : undefined}
              >
                {num}
              </button>
            </li>
          );
        })}

        <li className={s.paginationItem}>
          <button
            className={`${s.paginationButton} ${page === totalPages ? s.disabled : ''}`}
            onClick={handleNextClick}
            disabled={page === totalPages}
            aria-label="Следующая страница"
          >
            {'>'}
          </button>
        </li>
      </ul>
    </nav>
  );
};